// Paddle.js (v2), loaded once for the whole studio: Plan & billing opens its
// checkout with it, and Paddle Retain shows its in-app notices with it (a
// payment that failed, a card about to expire) to a customer who pays or paid
// through Paddle. Retain knows the customer by their Paddle id (pwCustomer).
// Paddle.Initialize runs once per page load; a customer known later goes in
// with Paddle.Update. https://developer.paddle.com/paddlejs/include-paddlejs

import { router } from "@inertiajs/react"

export type PaddleSettings = { client_token: string; environment: string; customer?: string | null }
export type PaddleEvent = { name: string }

export type PaddleApi = {
  Environment: { set: (environment: string) => void }
  Initialize: (options: { token: string; pwCustomer: { id?: string }; eventCallback: (event: PaddleEvent) => void }) => void
  Update: (options: { pwCustomer: { id?: string } }) => void
  Checkout: {
    open: (options: { settings: { displayMode: "overlay"; theme: "light" | "dark"; locale: string }; items: { priceId: string; quantity: number }[]; customer?: { id: string } }) => void
  }
}

const listeners = new Set<(event: PaddleEvent) => void>()
let loading: Promise<PaddleApi> | null = null
let customer: string | null = null

export function loadPaddle(settings: PaddleSettings): Promise<PaddleApi> {
  if (loading) {
    void loading.then((Paddle) => identify(Paddle, settings.customer))
    return loading
  }
  loading = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = "https://cdn.paddle.com/paddle/v2/paddle.js"
    script.async = true
    script.onload = () => {
      const Paddle = (window as unknown as { Paddle: PaddleApi }).Paddle
      Paddle.Environment.set(settings.environment)
      customer = settings.customer ?? null
      Paddle.Initialize({
        token: settings.client_token,
        pwCustomer: customer ? { id: customer } : {},
        eventCallback: (event) => listeners.forEach((listener) => listener(event)),
      })
      resolve(Paddle)
    }
    script.onerror = () => {
      loading = null
      reject(new Error("Paddle.js didn't load"))
    }
    document.head.appendChild(script)
  })
  return loading
}

// Paddle's events (a checkout opened, completed), to whoever listens
export function onPaddleEvent(listener: (event: PaddleEvent) => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function identify(Paddle: PaddleApi, next: string | null | undefined) {
  if (!next || next === customer) return
  customer = next
  Paddle.Update({ pwCustomer: { id: next } })
}

// Retain on every page of a customer of Paddle's: the server shares
// `retain` only then (ApplicationController), so no one else loads Paddle.js
export function installRetain() {
  router.on("navigate", (event) => {
    const retain = (event.detail.page.props as { retain?: PaddleSettings | null }).retain
    if (retain?.customer) void loadPaddle(retain).catch(() => {})
  })
}
